import './style.css'
import * as THREE from 'three'
import gsap from "gsap";
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshSurfaceSampler } from 'three/examples/jsm/math/MeshSurfaceSampler.js';
import { GUI } from 'dat.gui'
import Stats from 'three/examples/jsm/libs/stats.module'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import sources from './sources';

/**
 * Base
 */
// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

/**
 * Stats
 */
 const stats = Stats()
 document.body.appendChild(stats.dom)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Mouse move
 */
// let mouse = new THREE.Vector2();
// window.addEventListener('mousemove', (event) => {
//     event.preventDefault();
//     mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
//     mouse.y = - (event.clientY / window.innerHeight) * 2 + 1;

//     mesh.position.x = mouse.x;
//     mesh.position.y = mouse.y;

// })

// window.addEventListener('scroll', (event) => {
//     console.log(window.scrollY);
//     mesh.rotation.x = -window.scrollY * 0.1;
// })

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
camera.position.z = 3
scene.add(camera)

/**
 * Lights
 */
 const light = new THREE.AmbientLight( 0x404040 ); // soft white light
 scene.add( light ); 
 const directionalLight = new THREE.DirectionalLight( 0xffffff, 1.0 );
 directionalLight.position.set(1,0,0)
 scene.add( directionalLight );

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true

/**
 * 3D Model
 */
const gltfLoader = new GLTFLoader();
let models = [];
let loaded = 0;
const toLoad = sources.length;
for (const source of sources) {
    if (source.type === "gltfModel") {
        gltfLoader.load(source.path, (model) => {
            sourceLoaded(model);
        })
    }
}

const sourceLoaded = (model) => {
    console.log("** source loaded **");
    let model3D = model.scene.children[0]
    models.push(model3D);
    model.scene.position.z = -10;
    model.scene.rotation.y = 10;

    loaded++;
    if (loaded === toLoad) {
        init();
    }
}
let sampler
let samplers = []
let geometry
let number = 500
let pointPos
let particleSizes
let _position
let _normal

const init = () => {
    console.log("** ALL RESOURCES LOADED **")
    for (let i = 0; i < models.length; i++) {
        samplers[i] = new MeshSurfaceSampler(models[i]).setWeightAttribute('uv').build();
    }
    sampler = samplers[0];
    
    geometry = new THREE.IcosahedronGeometry(0.1, 10);

    const material = new THREE.MeshStandardMaterial({})
    
    const customUniforms = {
        rotation: {value: 0.0},
        uTime: {value: 0},
        uSize: {value: 1.},
        uSizeParticles : {value: 0.3},
        uNoise: {value: 0.05},
        uSpeedNoise: {value: 0.1}
    }

    material.onBeforeCompile = (shader) => {
        shader.uniforms.uTime = customUniforms.uTime;
        shader.uniforms.uSize = customUniforms.uSize;
        shader.uniforms.uSizeParticles = customUniforms.uSizeParticles;
        shader.uniforms.uNoise = customUniforms.uNoise;
        shader.uniforms.uSpeedNoise = customUniforms.uSpeedNoise;
        shader.vertexShader = shader.vertexShader.replace(
            '#include <common>',
            `
                #include <common>
    
                uniform float uTime;
                uniform float uSize;
                uniform float uSizeParticles;
                uniform float uNoise;
                uniform float uSpeedNoise;
                attribute vec3 aSpherePos;
                attribute vec3 aColor;
                attribute float aSize;
    
                varying vec3 vColor;

                vec3 mod289(vec3 x) {
                    return x - floor(x * (1.0 / 289.0)) * 289.0;
                }
                
                vec2 mod289(vec2 x) {
                    return x - floor(x * (1.0 / 289.0)) * 289.0;
                }
                
                vec3 permute(vec3 x) {
                    return mod289(((x*34.0)+1.0)*x);
                }
                
                float noise(vec2 v)
                {
                    const vec4 C = vec4(0.211324865405187,  // (3.0-sqrt(3.0))/6.0
                                      0.366025403784439,  // 0.5*(sqrt(3.0)-1.0)
                                     -0.577350269189626,  // -1.0 + 2.0 * C.x
                                      0.024390243902439); // 1.0 / 41.0
                    // First corner
                    vec2 i  = floor(v + dot(v, C.yy) );
                    vec2 x0 = v -   i + dot(i, C.xx);
                
                    // Other corners
                    vec2 i1;
                    //i1.x = step( x0.y, x0.x ); // x0.x > x0.y ? 1.0 : 0.0
                    //i1.y = 1.0 - i1.x;
                    i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
                    // x0 = x0 - 0.0 + 0.0 * C.xx ;
                    // x1 = x0 - i1 + 1.0 * C.xx ;
                    // x2 = x0 - 1.0 + 2.0 * C.xx ;
                    vec4 x12 = x0.xyxy + C.xxzz;
                    x12.xy -= i1;
                
                    // Permutations
                    i = mod289(i); // Avoid truncation effects in permutation
                    vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 ))
                        + i.x + vec3(0.0, i1.x, 1.0 ));
                
                    vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
                    m = m*m ;
                    m = m*m ;
                
                    // Gradients: 41 points uniformly over a line, mapped onto a diamond.
                    // The ring size 17*17 = 289 is close to a multiple of 41 (41*7 = 287)
                
                    vec3 x = 2.0 * fract(p * C.www) - 1.0;
                    vec3 h = abs(x) - 0.5;
                    vec3 ox = floor(x + 0.5);
                    vec3 a0 = x - ox;
                
                    // Normalise gradients implicitly by scaling m
                    // Approximation of: m *= inversesqrt( a0*a0 + h*h );
                    m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
                
                    // Compute final noise value at P
                    vec3 g;
                    g.x  = a0.x  * x0.x  + h.x  * x0.y;
                    g.yz = a0.yz * x12.xz + h.yz * x12.yw;
                    return 130.0 * dot(m, g);
                }
                
                vec3 curl(float	x,	float	y,	float	z)
                {
                
                    float	eps	= 1., eps2 = 2. * eps;
                    float	n1,	n2,	a,	b;
                
                    x += uTime * uSpeedNoise;
                    y += uTime * uSpeedNoise;
                    z += uTime * uSpeedNoise;
                
                    vec3	curl = vec3(0.);
                
                    n1	=	noise(vec2( x,	y	+	eps ));
                    n2	=	noise(vec2( x,	y	-	eps ));
                    a	=	(n1	-	n2)/eps2;
                
                    n1	=	noise(vec2( x,	z	+	eps));
                    n2	=	noise(vec2( x,	z	-	eps));
                    b	=	(n1	-	n2)/eps2;
                
                    curl.x	=	a	-	b;
                
                    n1	=	noise(vec2( y,	z	+	eps));
                    n2	=	noise(vec2( y,	z	-	eps));
                    a	=	(n1	-	n2)/eps2;
                
                    n1	=	noise(vec2( x	+	eps,	z));
                    n2	=	noise(vec2( x	+	eps,	z));
                    b	=	(n1	-	n2)/eps2;
                
                    curl.y	=	a	-	b;
                
                    n1	=	noise(vec2( x	+	eps,	y));
                    n2	=	noise(vec2( x	-	eps,	y));
                    a	=	(n1	-	n2)/eps2;
                
                    n1	=	noise(vec2(  y	+	eps,	z));
                    n2	=	noise(vec2(  y	-	eps,	z));
                    b	=	(n1	-	n2)/eps2;
                
                    curl.z	=	a	-	b;
                
                    return	curl;
                }
    
            `
        )
        shader.vertexShader = shader.vertexShader.replace(
            '#include <begin_vertex>',
            `
                vec3 noise = uNoise * curl(aSpherePos.x, aSpherePos.y, aSpherePos.z);

                vec3 transformed = (aSize * uSizeParticles) * vec3(position) + aSpherePos + noise;
            `
        )
        shader.vertexShader = shader.vertexShader.replace(
            '#include <project_vertex>',
            `
                vec4 mvPosition = vec4(uSize * transformed, 1.0 );
    
                #ifdef USE_INSTANCING
                
                    // mvPosition = instanceMatrix * mvPosition;
                
                #endif
                
                mvPosition = modelViewMatrix * mvPosition; 

                gl_PointSize = 0.1 * (1. / - mvPosition.z);
                
                gl_Position = projectionMatrix * mvPosition;
    
                vColor = aColor;
            `
        )
    }
    
    const mesh = new THREE.InstancedMesh(geometry, material, number)

    pointPos = new Float32Array(number*3);
    particleSizes = new Float32Array(number);
    _position = new THREE.Vector3();
    _normal = new THREE.Vector3();
    for (let i = 0; i < number; i++) {
        sampler.sample(_position, _normal);

        let s = Math.random() * (0.2 - 0.1) + 0.1; 
        particleSizes.set([s], i);

        pointPos.set([_position.x, _position.y, _position.z], i*3)
    }
    geometry.setAttribute('aSpherePos', new THREE.InstancedBufferAttribute(pointPos, 3))
    geometry.setAttribute('aSize', new THREE.InstancedBufferAttribute(particleSizes, 1))

    scene.add(mesh)

    /**
     * Renderer
     */
    const renderer = new THREE.WebGLRenderer({
        canvas: canvas,
        antialias: false,
    })
    renderer.setSize(sizes.width, sizes.height)
    renderer.setClearColor(0xffffff, 1)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1))

    /*
     * Shapes animations
     */
    let d = 3.0;
    let shapes = {
        monkey: () => {
            gsap.to(customUniforms.uSize, {value: 2.0, duration: d, ease: "power2.out"})
            gsap.to(customUniforms.uNoise, {value: 2.0, duration: d, ease: "power2.out"})
            gsap.to(customUniforms.rotation, {value: 10.0, duration: d, ease: "power2.out", onComplete() {
                sampler = samplers[0];
                for (let i = 0; i < number; i++) {
                    sampler.sample(_position, _normal);
                    pointPos.set([_position.x, _position.y, _position.z], i*3)
                }
                geometry.setAttribute('aSpherePos', new THREE.InstancedBufferAttribute(pointPos, 3))

                gsap.to(customUniforms.uSize, {value: 1.0, duration: d/4, ease: "power2.out"})
                gsap.to(customUniforms.uNoise, {value: 0.0, duration: d/4, ease: "power2.out"})
                gsap.to(customUniforms.rotation, {value: 0.01, duration: d/4, ease: "power2.out"})
            }})
        },
        sphere: () => {
            gsap.to(customUniforms.uSize, {value: 2.0, duration: d, ease: "power2.out"})
            gsap.to(customUniforms.uNoise, {value: 2.0, duration: d, ease: "power2.out"})
            gsap.to(customUniforms.rotation, {value: 10.0, duration: d, ease: "power2.out", onComplete() {
                sampler = samplers[1];
                for (let i = 0; i < number; i++) {
                    sampler.sample(_position, _normal);
                    pointPos.set([_position.x, _position.y, _position.z], i*3)
                }
                geometry.setAttribute('aSpherePos', new THREE.InstancedBufferAttribute(pointPos, 3))
    
                gsap.to(customUniforms.uSize, {value: 1.0, duration: d/4, ease: "power2.out"})
                gsap.to(customUniforms.uNoise, {value: 0.0, duration: d/4, ease: "power2.out"})
                gsap.to(customUniforms.rotation, {value: 0.01, duration: d/4, ease: "power2.out"})
            }})
        },
        heart: () => {
            gsap.to(customUniforms.uSize, {value: 2.0, duration: d, ease: "power2.out"})
            gsap.to(customUniforms.uNoise, {value: 2.0, duration: d, ease: "power2.out"})
            gsap.to(customUniforms.rotation, {value: 10.0, duration: d, ease: "power2.out", onComplete() {
                sampler = samplers[2];
                for (let i = 0; i < number; i++) {
                    sampler.sample(_position, _normal);
                    pointPos.set([_position.x, _position.y, _position.z], i*3)
                }
                geometry.setAttribute('aSpherePos', new THREE.InstancedBufferAttribute(pointPos, 3))
    
                gsap.to(customUniforms.uSize, {value: 1.0, duration: d/4, ease: "power2.out"})
                gsap.to(customUniforms.uNoise, {value: 0.0, duration: d/4, ease: "power2.out"})
                gsap.to(customUniforms.rotation, {value: 0.01, duration: d/4, ease: "power2.out"})
            }})
        }
    }
    
    /**
     * Dat gui
     */
     const gui = new GUI();
     const meshfolder = gui.addFolder('Mesh')
     meshfolder.add(customUniforms.uSize, 'value', 0.1, 2.0).name('Size').listen()
     meshfolder.add(customUniforms.uSizeParticles, 'value', 0.1, 2.0).name('Particles Size').listen()
     meshfolder.add(customUniforms.rotation, 'value', 0.0, 20.0).step(0.001).name('Rotation').listen()
     const noisefolder = gui.addFolder('Noise')
     noisefolder.add(customUniforms.uNoise, 'value', 0.0, 2.0).step(0.01).name('Noise').listen()
     noisefolder.add(customUniforms.uSpeedNoise, 'value', 0.0, 2.0).step(0.001).name('Noise Speed').listen()
     const animfolder = gui.addFolder('Animations')
     animfolder.add(shapes, 'monkey').name("Monkey");
     animfolder.add(shapes, 'sphere').name("Sphere");
     animfolder.add(shapes, 'heart').name("Heart");
    
    /**
     * Animate
     */
    const clock = new THREE.Clock()
    let lastElapsedTime = 0
    
    const tick = () =>
    {
        const elapsedTime = clock.getElapsedTime()
        const deltaTime = elapsedTime - lastElapsedTime
        lastElapsedTime = elapsedTime
    
        // Update controls
        controls.update()
    
        // Update stats
        stats.update()
    
        customUniforms.uTime.value = elapsedTime
    
        // mesh.rotation.y += deltaTime * customUniforms.rotation.value * 180 * Math.PI / 180;
        mesh.rotation.y += 0.001;
    
        // material.needsUpdate = true;
    
        // Render
        renderer.render(scene, camera)
    
        // Call tick again on the next frame
        window.requestAnimationFrame(tick)
    }
    
    tick()    
}